import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircleQuestion, X, ArrowUp, Loader2 } from "lucide-react";
import { useChat } from "./useChat";
import { ChatText } from "./ChatText";
import { WHATSAPP_URL } from "@/components/landing/data";

const SUGGESTIONS = ["Il laser fa male?", "Ho paura del dentista, come fate?", "Le gengive mi sanguinano: è grave?", "Cos'è l'igiene GBT?"];

const Bubble = ({ m, i }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${i}`}>
    <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-[14px] leading-relaxed ${m.role === "user" ? "bg-[#1B3B28] text-[#FCFBF7] rounded-br-md" : "bg-[#F4F3EC] text-[#1E2922] rounded-bl-md border border-[#1B3B28]/5"}`}>
      {m.content ? <ChatText text={m.content} /> : <span className="inline-flex gap-1 py-1" aria-label="Sto scrivendo">{[0, 1, 2].map((d) => <span key={d} className="w-1.5 h-1.5 rounded-full bg-[#3E6B48] animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />)}</span>}
    </div>
  </div>
);

const Intro = ({ onPick }) => (
  <div className="space-y-4" data-testid="chat-intro">
    <div className="rounded-2xl bg-[#F4F3EC] border border-[#1B3B28]/5 px-4 py-3 text-[14px] leading-relaxed text-[#1E2922]">
      Ciao! Sono l'assistente del Dott. Nicola Mauro. Chiedimi pure di gengive, laser, igiene GBT o di come affrontiamo la paura del dentista.
    </div>
    <div className="flex flex-wrap gap-2">
      {SUGGESTIONS.map((s, i) => (
        <button key={s} onClick={() => onPick(s)} data-testid={`chat-suggestion-${i}`}
          className="text-[13px] rounded-full border border-[#3E6B48]/30 px-3 py-1.5 text-[#1B3B28] hover:bg-[#E6ECE4] transition-colors">{s}</button>
      ))}
    </div>
  </div>
);

const Composer = ({ busy, onSend }) => {
  const [text, setText] = useState("");
  const submit = (e) => { e.preventDefault(); if (!text.trim() || busy) return; onSend(text); setText(""); };
  return (
    <form onSubmit={submit} className="flex items-end gap-2 border-t border-[#1B3B28]/10 p-3 bg-[#FCFBF7]">
      <textarea value={text} onChange={(e) => setText(e.target.value.slice(0, 800))} rows={1} placeholder="Scrivi la tua domanda…" data-testid="chat-input"
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) submit(e); }}
        className="flex-1 resize-none max-h-28 rounded-2xl bg-[#F4F3EC] px-4 py-3 text-base text-[#1E2922] placeholder:text-[#526356] outline-none focus:ring-2 focus:ring-[#86C89B]" />
      <button type="submit" disabled={busy || !text.trim()} data-testid="chat-send-btn" aria-label="Invia"
        className="grid place-items-center w-11 h-11 rounded-full bg-[#1B3B28] text-[#FCFBF7] disabled:opacity-40 transition-opacity">
        {busy ? <Loader2 size={18} className="animate-spin" /> : <ArrowUp size={18} />}
      </button>
    </form>
  );
};

const Panel = ({ onClose }) => {
  const { messages, busy, error, send } = useChat(true);
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [messages]);
  return (
    <motion.div initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.97 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }} role="dialog" aria-label="Assistente virtuale" data-testid="chat-panel"
      className="fixed z-[70] inset-0 sm:inset-auto sm:bottom-28 sm:right-6 sm:w-[400px] sm:h-[600px] sm:max-h-[calc(100vh-8rem)] flex flex-col bg-[#FCFBF7] sm:rounded-3xl border border-[#1B3B28]/10 shadow-[0_40px_90px_-30px_rgba(15,35,23,0.55)] overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-5 py-4 bg-[#1B3B28] text-[#FCFBF7]">
        <div>
          <p className="f-serif text-xl leading-none">Domande sulle gengive?</p>
          <p className="text-xs text-[#FCFBF7]/70 mt-1">Assistente virtuale · risponde subito</p>
        </div>
        <button onClick={onClose} data-testid="chat-close-btn" aria-label="Chiudi" className="grid place-items-center w-10 h-10 rounded-full bg-[#FCFBF7]/10 hover:bg-[#FCFBF7]/20 transition-colors"><X size={18} /></button>
      </div>
      <div data-lenis-prevent className="flex-1 overflow-y-auto px-4 py-5 space-y-3" data-testid="chat-messages">
        {messages.length === 0 ? <Intro onPick={send} /> : messages.map((m, i) => <Bubble key={i} m={m} i={i} />)}
        {error && <p className="text-sm text-[#8A3B2E] bg-[#F7E9E5] rounded-xl px-4 py-3" data-testid="chat-error">{error} <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="underline">Scrivici su WhatsApp</a></p>}
        <div ref={end} />
      </div>
      <p className="px-5 pb-1 pt-2 text-[12px] text-[#526356] bg-[#FCFBF7]">Risposte informative: non sostituiscono la visita.</p>
      <Composer busy={busy} onSend={send} />
    </motion.div>
  );
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("open-chat", onOpen);
    return () => window.removeEventListener("open-chat", onOpen);
  }, []);
  useEffect(() => {
    if (!open) return undefined;
    document.body.classList.add("chat-open");
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); document.body.classList.remove("chat-open"); };
  }, [open]);
  return (
    <>
      <motion.button onClick={() => setOpen((o) => !o)} data-testid="chat-fab" aria-label={open ? "Chiudi assistente" : "Apri assistente virtuale"}
        initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 2.6, type: "spring", stiffness: 180, damping: 14 }}
        whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.95 }}
        className="fixed bottom-6 right-[6.5rem] z-50 hidden lg:flex items-center justify-center gap-2 h-16 w-16 sm:w-auto sm:pl-5 sm:pr-6 rounded-full bg-[#FCFBF7] text-[#1B3B28] border border-[#1B3B28]/10 shadow-[0_20px_40px_-12px_rgba(15,35,23,0.35)]">
        {open ? <X size={20} /> : <MessageCircleQuestion size={20} />}
        <span className="text-sm font-medium hidden sm:inline">{open ? "Chiudi" : "Hai domande?"}</span>
      </motion.button>
      <AnimatePresence>{open && <Panel onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
};
